import { MeetingTemplate, ParticipantRole } from '@/types/meeting';

const now = new Date();
const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

export const meetingTemplates: MeetingTemplate[] = [
  {
    id: 'parent-teacher',
    name: 'Parent-Teacher Conference',
    type: 'PARENT_TEACHER',
    purpose: 'Discuss student academic progress, behavior, and development',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.PARENT, ParticipantRole.STUDENT],
    defaultDuration: 30,
    description: 'Standard parent-teacher conference to review student progress and set goals',
    agendaTemplate: [
      {
        title: 'Academic Progress Review',
        duration: 10,
        description: 'Review grades, assignments, and academic performance'
      },
      {
        title: 'Behavioral Assessment',
        duration: 10,
        description: 'Discuss classroom behavior and social interactions'
      },
      {
        title: 'Goal Setting',
        duration: 10,
        description: 'Establish academic and behavioral goals'
      }
    ],
    createdAt: lastWeek,
    updatedAt: now,
  },
  {
    id: 'iep',
    name: 'IEP Meeting',
    type: 'IEP',
    purpose: 'Review and update Individual Education Plan',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.PARENT, ParticipantRole.COUNSELOR, ParticipantRole.ADMIN],
    defaultDuration: 60,
    description: 'Comprehensive review of student Individual Education Plan',
    agendaTemplate: [
      {
        title: 'Current Performance Review',
        duration: 15,
        description: 'Review current academic performance and IEP goals'
      },
      {
        title: 'Accommodations Discussion',
        duration: 20,
        description: 'Review and adjust current accommodations'
      },
      {
        title: 'Goal Setting',
        duration: 15,
        description: 'Establish new IEP goals and objectives'
      },
      {
        title: 'Service Planning',
        duration: 10,
        description: 'Plan support services and schedule'
      }
    ],
    createdAt: lastWeek,
    updatedAt: lastWeek,
  },
  {
    id: 'crisis-response',
    name: 'Crisis Response Meeting',
    type: 'ADMINISTRATIVE',
    purpose: 'Address and plan response to school crisis situations',
    requiredRoles: [ParticipantRole.ADMIN, ParticipantRole.COUNSELOR, ParticipantRole.DEPARTMENT_HEAD],
    defaultDuration: 45,
    description: 'Emergency meeting to coordinate crisis response and support',
    agendaTemplate: [
      {
        title: 'Situation Assessment',
        duration: 15,
        description: 'Review current situation and immediate concerns'
      },
      {
        title: 'Response Planning',
        duration: 15,
        description: 'Develop immediate action plans and assign responsibilities'
      },
      {
        title: 'Communication Strategy',
        duration: 15,
        description: 'Plan communication with staff, students, and families'
      }
    ],
    createdAt: lastWeek,
    updatedAt: now,
  },
  {
    id: 'staff-collaboration',
    name: 'Staff Collaboration',
    type: 'STAFF',
    purpose: 'Foster collaboration and alignment among teaching staff',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.DEPARTMENT_HEAD],
    defaultDuration: 60,
    description: 'Regular staff meeting for curriculum alignment and best practices',
    agendaTemplate: [
      {
        title: 'Department Updates',
        duration: 15,
        description: 'Share updates from each department'
      },
      {
        title: 'Best Practices Exchange',
        duration: 25,
        description: 'Share and discuss teaching strategies'
      },
      {
        title: 'Curriculum Alignment',
        duration: 20,
        description: 'Ensure curriculum consistency across classes'
      }
    ],
    createdAt: lastWeek,
    updatedAt: lastWeek,
  },
  {
    id: 'behavior-intervention',
    name: 'Behavior Intervention',
    type: 'DISCIPLINARY',
    purpose: 'Address behavioral concerns and develop intervention strategies',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.COUNSELOR, ParticipantRole.ADMIN, ParticipantRole.PARENT],
    defaultDuration: 45,
    description: 'Meeting to discuss and address student behavioral concerns',
    agendaTemplate: [
      {
        title: 'Behavior Review',
        duration: 15,
        description: 'Review specific behavioral incidents and patterns'
      },
      {
        title: 'Impact Assessment',
        duration: 10,
        description: 'Discuss impact on learning environment'
      },
      {
        title: 'Intervention Planning',
        duration: 20,
        description: 'Develop behavior intervention strategies'
      }
    ],
    createdAt: lastWeek,
    updatedAt: now,
  },
  {
    id: 'academic-planning',
    name: 'Academic Planning',
    type: 'ACADEMIC_REVIEW',
    purpose: 'Plan academic strategies and support',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.COUNSELOR, ParticipantRole.STUDENT],
    defaultDuration: 45,
    description: 'Strategic planning for academic success',
    agendaTemplate: [
      {
        title: 'Academic Assessment',
        duration: 15,
        description: 'Review current academic standing'
      },
      {
        title: 'Support Planning',
        duration: 15,
        description: 'Identify needed academic support'
      },
      {
        title: 'Goal Setting',
        duration: 15,
        description: 'Set specific academic goals'
      }
    ],
    createdAt: lastWeek,
    updatedAt: lastWeek,
  },
  {
    id: 'parent-workshop',
    name: 'Parent Workshop',
    type: 'PARENT_TEACHER',
    purpose: 'Educational workshop for parents',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.ADMIN, ParticipantRole.PARENT],
    defaultDuration: 90,
    description: 'Workshop to help parents support student learning',
    agendaTemplate: [
      {
        title: 'Topic Introduction',
        duration: 20,
        description: 'Overview of workshop topic'
      },
      {
        title: 'Strategies Presentation',
        duration: 40,
        description: 'Present practical strategies'
      },
      {
        title: 'Q&A Session',
        duration: 30,
        description: 'Address parent questions'
      }
    ],
    createdAt: lastWeek,
    updatedAt: now,
  },
  {
    id: 'policy-review',
    name: 'Policy Review',
    type: 'ADMINISTRATIVE',
    purpose: 'Review and update school policies',
    requiredRoles: [ParticipantRole.ADMIN, ParticipantRole.DEPARTMENT_HEAD],
    defaultDuration: 60,
    description: 'Meeting to review and update school policies',
    agendaTemplate: [
      {
        title: 'Policy Review',
        duration: 20,
        description: 'Review current policies'
      },
      {
        title: 'Updates Discussion',
        duration: 25,
        description: 'Discuss needed policy updates'
      },
      {
        title: 'Implementation Planning',
        duration: 15,
        description: 'Plan policy implementation'
      }
    ],
    createdAt: lastWeek,
    updatedAt: lastWeek,
  },
  {
    id: 'graduation-planning',
    name: 'Graduation Planning',
    type: 'ADMINISTRATIVE',
    purpose: 'Plan graduation ceremony and activities',
    requiredRoles: [ParticipantRole.ADMIN, ParticipantRole.DEPARTMENT_HEAD, ParticipantRole.TEACHER],
    defaultDuration: 60,
    description: 'Coordinate graduation events and requirements',
    agendaTemplate: [
      {
        title: 'Timeline Review',
        duration: 15,
        description: 'Review graduation timeline'
      },
      {
        title: 'Event Planning',
        duration: 25,
        description: 'Plan ceremony and activities'
      },
      {
        title: 'Task Assignment',
        duration: 20,
        description: 'Assign responsibilities'
      }
    ],
    createdAt: lastWeek,
    updatedAt: now,
  },
  {
    id: 'volunteer-coordination',
    name: 'Volunteer Coordination',
    type: 'ADMINISTRATIVE',
    purpose: 'Coordinate school volunteer programs',
    requiredRoles: [ParticipantRole.ADMIN, ParticipantRole.TEACHER],
    defaultDuration: 45,
    description: 'Organize and manage school volunteer activities',
    agendaTemplate: [
      {
        title: 'Program Review',
        duration: 15,
        description: 'Review volunteer needs'
      },
      {
        title: 'Recruitment Planning',
        duration: 15,
        description: 'Plan volunteer recruitment'
      },
      {
        title: 'Schedule Coordination',
        duration: 15,
        description: 'Coordinate volunteer schedules'
      }
    ],
    createdAt: lastWeek,
    updatedAt: lastWeek,
  },
  {
    id: 'extracurricular-planning',
    name: 'Extracurricular Planning',
    type: 'CLUB',
    purpose: 'Plan extracurricular activities and events',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.STUDENT],
    defaultDuration: 45,
    description: 'Planning meeting for clubs and activities',
    agendaTemplate: [
      {
        title: 'Activity Planning',
        duration: 20,
        description: 'Plan upcoming activities'
      },
      {
        title: 'Resource Assessment',
        duration: 15,
        description: 'Review needed resources'
      },
      {
        title: 'Schedule Setting',
        duration: 10,
        description: 'Set activity schedule'
      }
    ],
    createdAt: lastWeek,
    updatedAt: now,
  },
  {
    id: 'student-support-team',
    name: 'Student Support Team (SST) Meeting',
    type: 'CLUB',
    purpose: 'Plan interventions for students requiring additional support',
    requiredRoles: [ParticipantRole.TEACHER, ParticipantRole.PARENT, ParticipantRole.STUDENT, ParticipantRole.COUNSELOR, ParticipantRole.ADMIN],
    defaultDuration: 60,
    description: 'Collaborative meeting to develop and implement support strategies for student success',
    agendaTemplate: [
      {
        title: 'Overview of Student Challenges',
        duration: 20,
        description: 'Review current academic, behavioral, or social challenges'
      },
      {
        title: 'Previous Interventions Review',
        duration: 15,
        description: 'Evaluate effectiveness of any previously implemented interventions'
      },
      {
        title: 'Solution Discussion',
        duration: 15,
        description: 'Brainstorm and discuss potential intervention strategies'
      },
      {
        title: 'Action Plan Development',
        duration: 10,
        description: 'Assign responsibilities and set timeline for interventions'
      }
    ],
    createdAt: lastWeek,
    updatedAt: lastWeek,
  }
];